import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import ReportCardPreviewModal from '../components/ReportCardPreviewModal';
import StudentAssignmentsSection from './student/StudentAssignmentsSection';
import StudentExtraClassesSection from './student/StudentExtraClassesSection';
import HostelSection from './student/HostelSection';
import TransportSection from './student/TransportSection';
import StudentELibrarySection from './student/StudentELibrarySection';
import StudentModulesSection from './student/StudentModulesSection';
import { useAuth } from '../context/AuthContext';
import studentPortalService from '../services/studentPortalService';
import schoolsService from '../services/schoolsService';
import gradesService from '../services/gradesService';
import {
  Student, BookOpen, CurrencyCircleDollar, CheckCircle,
  Megaphone, ChartBar, CalendarBlank, Warning, PlayCircle,
  CaretRight, GraduationCap, House, Eye, FileText, MapPin, CircleNotch, User, Wallet, ArrowClockwise
} from '@phosphor-icons/react';

// GES Grade colors
const GRADE_COLORS = {
  '1': '#059669', '2': '#10B981', '3': '#34D399',
  '4': '#FBBF24', '5': '#F59E0B', '6': '#F97316',
  '7': '#EF4444', '8': '#DC2626', '9': '#991B1B'
};

// Day order for timetable display
const DAY_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];

// Subject color mapping for visual distinction
const SUBJECT_COLORS = {
  'Mathematics': '#3B82F6',
  'English': '#10B981',
  'Science': '#F59E0B',
  'History': '#8B5CF6',
  'Physical Education': '#EC4899',
  'Art': '#F97316',
  'Computer Science': '#06B6D4',
  'Chemistry': '#14B8A6',
  'Physics': '#6366F1',
  'Biology': '#84CC16'
};

function getSubjectColor(subjectName) {
  return SUBJECT_COLORS[subjectName] || '#6B7280';
}

function formatCurrency(amount) {
  return `GHS ${amount.toLocaleString('en-GH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function StudentPortalPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState('dashboard'); // dashboard, grades, timetable, fees, attendance, assignments, wallet
  const [profile, setProfile] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [grades, setGrades] = useState(null);
  const [timetable, setTimetable] = useState(null);
  const [fees, setFees] = useState(null);
  const [attendance, setAttendance] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [academicTerms, setAcademicTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feesLoading, setFeesLoading] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [selectedAcademicTerm, setSelectedAcademicTerm] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewHTML, setPreviewHTML] = useState('');
  const [selectedAssignmentId, setSelectedAssignmentId] = useState(null);
  // Optional modules enrollment
  const [enrollmentStatus, setEnrollmentStatus] = useState(null);
  const [hostelData, setHostelData] = useState(null);
  const [transportData, setTransportData] = useState(null);

  // Check if user has required role
  useEffect(() => {
    if (user && user.role !== 'student') {
      toast.error('You do not have access to this page');
      navigate('/');
    }
  }, [user, navigate]);

  // Load profile and dashboard on mount
  useEffect(() => {
    Promise.all([
      studentPortalService.getProfile(),
      studentPortalService.getDashboard(),
      studentPortalService.getAnnouncements(5),
      schoolsService.listTerms(user.school_id),
      studentPortalService.getEnrollmentStatus()
    ])
      .then(([profileData, dashboardData, announcementsData, termsData, enrollmentData]) => {
        setProfile(profileData);
        setDashboard(dashboardData);
        setAnnouncements(announcementsData || []);
        setAcademicTerms(termsData || []);
        setEnrollmentStatus(enrollmentData);
        // Set the first term as default
        if (termsData && termsData.length > 0) {
          setSelectedAcademicTerm(termsData[0].id);
        }
      })
      .catch(err => {
        console.error(err);
        toast.error('Failed to load student data');
      })
      .finally(() => setLoading(false));
  }, [user?.school_id]);

  // Load hostel data if enrolled
  useEffect(() => {
    if (enrollmentStatus?.has_hostel) {
      studentPortalService.getHostelStatus()
        .then(data => setHostelData(data))
        .catch(err => console.error('Not enrolled in hostel'));
    }
  }, [enrollmentStatus?.has_hostel]);

  // Load transport data if enrolled  
  useEffect(() => {
    if (enrollmentStatus?.has_transport) {
      studentPortalService.getTransportStatus()
        .then(data => setTransportData(data))
        .catch(err => console.error('Not enrolled in transport'));
    }
  }, [enrollmentStatus?.has_transport]);

  // Load grades for selected academic term
  const loadGrades = useCallback(() => {
    if (!selectedAcademicTerm) return;
    studentPortalService.getGrades(selectedAcademicTerm)
      .then(data => setGrades(data))
      .catch(err => toast.error('Failed to load grades'));
  }, [selectedAcademicTerm]);

  // Load timetable
  const loadTimetable = useCallback(() => {
    studentPortalService.getTimetable()
      .then(data => setTimetable(data))
      .catch(err => toast.error('Failed to load timetable'));
  }, []);

  // Load fees for selected academic term
  const loadFees = useCallback(() => {
    if (!selectedAcademicTerm) return;
    setFeesLoading(true);
    studentPortalService.getFees(selectedAcademicTerm)
      .then(data => {
        setFees(data);
        setFeesLoading(false);
      })
      .catch(err => {
        console.error('Failed to load fees:', err);
        toast.error('Failed to load fees');
        setFeesLoading(false);
      });
  }, [selectedAcademicTerm]);

  // Load attendance for selected academic term
  const loadAttendance = useCallback(() => {
    if (!selectedAcademicTerm) return;
    studentPortalService.getAttendance(30, selectedAcademicTerm)
      .then(data => setAttendance(data))
      .catch(err => toast.error('Failed to load attendance'));
  }, [selectedAcademicTerm]);

  // Download report card as PDF
  // Preview report card as HTML modal
  const previewReportCard = useCallback(async () => {
    if (!profile || !profile.id) {
      toast.error('Student information not loaded');
      return;
    }
    
    setDownloadingPDF(true);
    try {
      const data = await gradesService.previewReportCard(profile.id, selectedAcademicTerm);
      setPreviewHTML(data.html);
      setShowPreviewModal(true);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setDownloadingPDF(false);
    }
  }, [profile, selectedAcademicTerm]);

  // Load data based on view
  useEffect(() => {
    if (view === 'dashboard' && selectedAcademicTerm) {
      // Reload dashboard when term changes
      studentPortalService.getDashboard(selectedAcademicTerm)
        .then(data => setDashboard(data))
        .catch(err => console.error(err));
    }
    if (view === 'grades') loadGrades();
    if (view === 'timetable' && !timetable) loadTimetable();
    if (view === 'fees' && selectedAcademicTerm) loadFees();
    if (view === 'attendance' && selectedAcademicTerm) loadAttendance();
  }, [view, selectedAcademicTerm, timetable, loadGrades, loadTimetable, loadFees, loadAttendance]);

  const getStatusColor = (status) => {
    const colors = {
      present: '#059669',
      absent: '#DC2626',
      late: '#D97706',
      excused: '#3B82F6'
    };
    return colors[status] || '#A1A1AA';
  };

  const getTodayDay = () => {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return days[new Date().getDay()];
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading" data-testid="student-portal-loading">Loading student portal...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page student-portal" data-testid="student-portal">
        <div className="page-header">
          <div>
            <h1>Student Portal</h1>
            <p>Welcome, {profile?.first_name}! View your academic progress</p>
          </div>
        </div>

        {/* View Tabs */}
        <div className="tabs-container">
          <button 
            className={`tab-btn ${view === 'dashboard' ? 'active' : ''}`}
            onClick={() => setView('dashboard')}
            data-testid="tab-dashboard"
          >
            <House size={18} /> Dashboard
          </button>
          <button 
            className={`tab-btn ${view === 'grades' ? 'active' : ''}`}
            onClick={() => setView('grades')}
            data-testid="tab-grades"
          >
            <BookOpen size={18} /> My Grades
          </button>
          <button 
            className={`tab-btn ${view === 'timetable' ? 'active' : ''}`}
            onClick={() => setView('timetable')}
            data-testid="tab-timetable"
          >
            <CalendarBlank size={18} /> Timetable
          </button>
          <button
            className={`tab-btn ${view === 'fees' ? 'active' : ''}`}
            onClick={() => setView('fees')}
            data-testid="tab-fees"
          >
            <CurrencyCircleDollar size={18} /> Fees
          </button>
          <button 
            className={`tab-btn ${view === 'attendance' ? 'active' : ''}`}
            onClick={() => setView('attendance')}
            data-testid="tab-attendance"
          >
            <CheckCircle size={18} /> Attendance
          </button>
          <button 
            className={`tab-btn ${view === 'extra-classes' ? 'active' : ''}`}
            onClick={() => setView('extra-classes')}
            data-testid="tab-extra-classes"
          >
            <BookOpen size={18} /> Extra Classes
          </button>
          <button 
            className={`tab-btn ${view === 'library' ? 'active' : ''}`}
            onClick={() => setView('library')}
            data-testid="tab-library"
          >
            <BookOpen size={18} /> E-Library
          </button>
          <button
            className={`tab-btn ${view === 'modules' ? 'active' : ''}`}
            onClick={() => setView('modules')}
            data-testid="tab-modules"
          >
            <PlayCircle size={18} /> Course Modules
          </button>
          <button
            className="tab-btn"
            onClick={() => navigate('/canteen')}
            data-testid="tab-wallet"
          >
            <Wallet size={18} /> Canteen
          </button>
          {enrollmentStatus?.has_hostel && (
            <button 
              className={`tab-btn ${view === 'hostel' ? 'active' : ''}`}
              onClick={() => setView('hostel')}
              data-testid="tab-hostel"
            >
              <House size={18} style={{ color: '#8B4513' }} /> Hostel
            </button>
          )}
          {enrollmentStatus?.has_transport && (
            <button 
              className={`tab-btn ${view === 'transport' ? 'active' : ''}`}
              onClick={() => setView('transport')}
              data-testid="tab-transport"
            >
              <MapPin size={18} /> Transport
            </button>
          )}
        </div>

        {/* Dashboard View */}
        {view === 'dashboard' && dashboard && (
          <div className="student-dashboard" data-testid="dashboard-view">
            {/* Student Info Card */}
            <div className="student-info-card">
              <div className="student-avatar">
                <GraduationCap size={40} weight="duotone" />
              </div>
              <div className="student-details">
                <h2>{dashboard.student.name}</h2>
                <p>Student ID: {dashboard.student.student_id}</p>
                <p>Class: {dashboard.student.class_name || 'Not Assigned'}</p>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="parent-stats-grid">
              <div className="parent-stat-card" onClick={() => setView('attendance')} data-testid="stat-attendance">
                <div className="stat-icon attendance">
                  <CheckCircle size={28} weight="duotone" />
                </div>
                <div className="stat-content">
                  <span className="stat-value">{dashboard.attendance.rate}%</span>
                  <span className="stat-label">Attendance Rate</span>
                  <span className="stat-detail">
                    {dashboard.attendance.present} present / {dashboard.attendance.total_days} days
                  </span>
                </div>
                <CaretRight size={20} className="stat-arrow" />
              </div>

              <div className="parent-stat-card" onClick={() => setView('grades')} data-testid="stat-grades">
                <div className="stat-icon grades">
                  <BookOpen size={28} weight="duotone" />
                </div>
                <div className="stat-content">
                  <span className="stat-value" style={{ color: GRADE_COLORS[dashboard.academics.overall_grade] }}>
                    Grade {dashboard.academics.overall_grade}
                  </span>
                  <span className="stat-label">{dashboard.academics.overall_average}% Average</span>
                  <span className="stat-detail">{dashboard.academics.grade_description}</span>
                </div>
                <CaretRight size={20} className="stat-arrow" />
              </div>

              {/* <div className="parent-stat-card" onClick={() => setView('fees')} data-testid="stat-fees">
                <div className={`stat-icon ${dashboard.fees.balance > 0 ? 'warning' : 'success'}`}>
                  <CurrencyCircleDollar size={28} weight="duotone" />
                </div>
                <div className="stat-content">
                  <span className="stat-value">{formatCurrency(dashboard.fees.balance)}</span>
                  <span className="stat-label">Fee Balance</span>
                  <span className="stat-detail">
                    {dashboard.fees.balance > 0 ? 'Outstanding' : 'Fully Paid'}
                  </span>
                </div>
                <CaretRight size={20} className="stat-arrow" />
              </div> */}

              <div className="parent-stat-card" onClick={() => setView('timetable')} data-testid="stat-timetable">
                <div className="stat-icon timetable">
                  <CalendarBlank size={28} weight="duotone" />
                </div>
                <div className="stat-content">
                  <span className="stat-value">{dashboard.today_classes}</span>
                  <span className="stat-label">Classes Today</span>
                  <span className="stat-detail">View full timetable</span>
                </div>
                <CaretRight size={20} className="stat-arrow" />
              </div>
            </div>

            {/* Recent Announcements */}
            {announcements.length > 0 && (
              <div className="announcements-section">
                <h3><Megaphone size={20} /> Recent Announcements</h3>
                <div className="announcement-list">
                  {announcements.slice(0, 3).map(announcement => (
                    <div key={announcement.id} className="announcement-item">
                      <span className={`announcement-type ${announcement.announcement_type}`}>
                        {announcement.announcement_type}
                      </span>
                      <h4>{announcement.title}</h4>
                      <p>{announcement.content.substring(0, 150)}...</p>
                      <span className="announcement-date">
                        {new Date(announcement.publish_date).toLocaleDateString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}


        {/* Grades View */}
        {view === 'grades' && grades && (
          <div className="grades-view" data-testid="grades-view">
            {/* Download Report Card */}
            <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', alignItems: 'center' }}>
              <div style={{ flex: 1 }}>
                <select 
                  value={selectedAcademicTerm || ''} 
                  onChange={(e) => setSelectedAcademicTerm(e.target.value)}
                  style={{ padding: '8px', borderRadius: '4px', border: '1px solid #0d0c0c', width: '100%', color: '#111827', backgroundColor: '#ffffff' }}
                  data-testid="academic-term-select"
                >
                  <option value="" disabled={academicTerms.length > 0}>Select Academic Term</option>
                  {academicTerms.length === 0 && (
                    <option value="" disabled>No terms available</option>
                  )}
                  {academicTerms.map(term => (
                    <option key={term.id} value={term.id}>
                      {term.term} - {term.academic_year}
                    </option>
                  ))}
                </select>
              </div>
              <button 
                onClick={previewReportCard}
                disabled={downloadingPDF || !selectedAcademicTerm}
                className="btn btn-secondary"
                data-testid="preview-reportcard-student-btn"
                style={{ whiteSpace: 'nowrap' }}
              >
                {downloadingPDF ? (
                  <span className="button-loading">
                    <CircleNotch size={16} weight="fill" className="spinner" />
                    Loading...
                  </span>
                ) : (
                  <>
                    <Eye size={18} /> Preview Report Card
                  </>
                )}
              </button>
            </div>

            {/* Overall Summary */}
            <div className="overall-grade-card">
              <div className="overall-header">
                <h3>Overall Performance</h3>
              </div>
              <div className="overall-content">
                <div 
                  className="overall-grade-badge"
                  style={{ backgroundColor: GRADE_COLORS[grades.overall.grade] }}
                >
                  {grades.overall.grade}
                </div>
                <div className="overall-details">
                  <span className="overall-percentage">{grades.overall.average}%</span>
                  <span className="overall-description">{grades.overall.description}</span>
                  <span className="overall-info">
                    {grades.overall.total_subjects} subjects, {grades.overall.total_assessments} assessments
                  </span>
                </div>
              </div>
            </div>

            {/* Subject Grades */}
            <div className="subjects-grades">
              <h3>Subject Performance</h3>
              {grades.subjects.length === 0 ? (
                <div className="empty-state">No grades recorded yet</div>
              ) : (
                <div className="subject-cards">
                  {grades.subjects.map(subject => (
                    <div key={subject.subject_name} className="subject-grade-card">
                      <div className="subject-header">
                        <div>
                          <h4>{subject.subject_name}</h4>
                          <span className="subject-code">{subject.subject_code}</span>
                        </div>
                        <span 
                          className="subject-grade"
                          style={{ backgroundColor: GRADE_COLORS[subject.average_grade] }}
                        >
                          {subject.average_grade}
                        </span>
                      </div>
                      <div className="subject-stats">
                        <span className="subject-avg">{subject.average_percentage}%</span>
                        <span className="subject-desc">{subject.average_description}</span>
                      </div>
                      <div className="subject-assessments">
                        {subject.assessments.map((assessment, idx) => (
                          <div key={idx} className="assessment-item">
                            <span className="assessment-type">{assessment.type.replace('_', ' ')}</span>
                            <span className="assessment-score">{assessment.score}/{assessment.max_score}</span>
                            <span 
                              className="assessment-grade"
                              style={{ color: GRADE_COLORS[assessment.grade] }}
                            >
                              ({assessment.percentage}%)
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Timetable View */}
        {view === 'timetable' && timetable && (
          <div className="timetable-view" data-testid="timetable-view">
            <div className="timetable-header">
              <div>
                <h3>Class: {timetable.class_name}</h3>
                {timetable.class_level && <span className="class-level">{timetable.class_level}</span>}
              </div>
              {timetable.schedule[getTodayDay()]?.length > 0 && (
                <div className="today-classes-count">
                  <span className="count-badge">{timetable.schedule[getTodayDay()].length}</span>
                  <span className="count-label">Classes today</span>
                </div>
              )}
            </div>

            {!timetable.class_name || timetable.class_name === 'Unknown' ? (
              <div className="empty-state-card">
                <CalendarBlank size={48} weight="duotone" />
                <h3>No Class Assigned</h3>
                <p>Contact your administrator to be assigned to a class</p>
              </div>
            ) : (
              <>
                {/* Today's Classes Section */}
                {timetable.schedule[getTodayDay()]?.length > 0 && (
                  <div className="todays-classes-section">
                    <h4 style={{ marginBottom: '12px', fontSize: '14px', fontWeight: '600', textTransform: 'uppercase', color: '#666' }}>
                      Today's Schedule
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '28px' }}>
                      {timetable.schedule[getTodayDay()].map((slot, idx) => (
                        <div 
                          key={idx} 
                          className="today-slot"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '12px',
                            borderLeft: `4px solid ${getSubjectColor(slot.subject_name)}`,
                            backgroundColor: '#f9fafb',
                            borderRadius: '6px',
                            gap: '12px'
                          }}
                        >
                          <div style={{ flex: '0 0 70px', textAlign: 'center' }}>
                            <div style={{ fontSize: '14px', fontWeight: '700', color: '#1f2937' }}>
                              {slot.start_time}
                            </div>
                            <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                              {slot.end_time}
                            </div>
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '15px', fontWeight: '600', color: '#1f2937', marginBottom: '2px' }}>
                              {slot.subject_name}
                            </div>
                            <div style={{ display: 'flex', gap: '16px', fontSize: '13px', color: '#6b7280' }}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><User size={14} /> {slot.teacher_name}</span>
                              {slot.room && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><MapPin size={14} /> {slot.room}</span>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Full Week Timetable */}
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ marginBottom: '14px', fontSize: '14px', fontWeight: '600', textTransform: 'uppercase', color: '#666' }}>
                    Weekly Schedule
                  </h4>
                </div>

                <div className="timetable-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '14px', marginBottom: '24px' }}>
                  {DAY_ORDER.map(day => {
                    const daySchedule = timetable.schedule[day] || [];
                    const isToday = getTodayDay() === day;
                    
                    return (
                      <div 
                        key={day} 
                        className={`timetable-day ${isToday ? 'today' : ''}`}
                        style={{
                          borderRadius: '8px',
                          border: isToday ? '2px solid #3B82F6' : '1px solid #e5e7eb',
                          backgroundColor: isToday ? '#eff6ff' : '#fff',
                          overflow: 'hidden'
                        }}
                      >
                        <div 
                          className="day-header"
                          style={{
                            padding: '12px',
                            backgroundColor: isToday ? '#3B82F6' : '#f3f4f6',
                            color: isToday ? '#fff' : '#1f2937',
                            borderBottom: '1px solid #e5e7eb',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                        >
                          <span className="day-name" style={{ fontWeight: '600', fontSize: '14px' }}>
                            {day.charAt(0).toUpperCase() + day.slice(1)}
                          </span>
                          {isToday && (
                            <span className="today-badge" style={{ fontSize: '11px', fontWeight: '600', backgroundColor: 'rgba(255,255,255,0.3)', padding: '2px 6px', borderRadius: '3px' }}>
                              TODAY
                            </span>
                          )}
                        </div>
                        <div className="day-schedule" style={{ padding: '8px' }}>
                          {daySchedule.length === 0 ? (
                            <div className="no-classes" style={{ padding: '16px 8px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>
                              No classes
                            </div>
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              {daySchedule.map((slot, idx) => (
                                <div 
                                  key={idx} 
                                  className="schedule-slot"
                                  style={{
                                    padding: '10px',
                                    borderLeft: `3px solid ${getSubjectColor(slot.subject_name)}`,
                                    backgroundColor: '#fafbfc',
                                    borderRadius: '4px',
                                    fontSize: '12px'
                                  }}
                                >
                                  <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px', fontWeight: '500' }}>
                                    {slot.start_time} - {slot.end_time}
                                  </div>
                                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#1f2937', marginBottom: '4px' }}>
                                    {slot.subject_name}
                                  </div>
                                  <div style={{ fontSize: '11px', color: '#6b7280' }}>
                                    {slot.teacher_name}
                                    {slot.room && <> • {slot.room}</>}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Periods Legend */}
                {timetable.periods.length > 0 && (
                  <div className="periods-legend" style={{ marginTop: '24px', padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                    <h4 style={{ marginBottom: '12px', fontSize: '13px', fontWeight: '600', textTransform: 'uppercase', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <CalendarBlank size={14} /> School Periods Reference
                    </h4>
                    <div className="periods-list" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px' }}>
                      {timetable.periods.map(period => (
                        <div key={period.id} className={`period-item ${period.period_type}`} style={{ padding: '10px', backgroundColor: '#fff', borderRadius: '6px', border: '1px solid #e5e7eb', fontSize: '12px' }}>
                          <div style={{ fontWeight: '600', color: '#1f2937', marginBottom: '4px' }}>
                            {period.name}
                          </div>
                          <div style={{ color: '#6b7280', fontSize: '11px' }}>
                            {period.start_time} - {period.end_time}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Fees View */}
        {view === 'fees' && (
          <div className="fees-view" data-testid="fees-view">
            {/* Academic Term Filter */}
            {academicTerms && academicTerms.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <select 
                  value={selectedAcademicTerm || ''} 
                  onChange={(e) => setSelectedAcademicTerm(e.target.value)}
                  style={{ padding: '8px', borderRadius: '4px', border: '1px solid #0d0c0c', width: '100%', maxWidth: '300px', color: '#111827', backgroundColor: '#ffffff' }}
                  data-testid="fee-term-filter"
                  disabled={feesLoading}
                >
                  <option value="" disabled={academicTerms.length > 0}>Select Academic Term</option>
                  {academicTerms.map(term => (
                    <option key={term.id} value={term.id}>
                      {term.term} - {term.academic_year}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Loading State */}
            {feesLoading && (
              <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
                <CircleNotch size={32} weight="fill" className="spinner" style={{ marginBottom: '12px', animation: 'spin 1s linear infinite' }} />
                <p>Loading fees data...</p>
              </div>
            )}

            {/* No Data State */}
            {!feesLoading && !fees && (
              <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
                <CurrencyCircleDollar size={48} weight="duotone" style={{ marginBottom: '12px' }} />
                <p>No fees data available. Please select an academic term.</p>
              </div>
            )}

            {/* Fees Content */}
            {!feesLoading && fees && (
              <>
                {/* Summary */}
                <div className="fee-summary-section">
                  <div className={`fee-balance-card ${fees.summary.balance > 0 ? 'outstanding' : 'paid'}`}>
                    <div className="balance-header">
                      <h3>Fee Balance</h3>
                      {fees.summary.balance > 0 ? (
                        <Warning size={24} weight="fill" />
                      ) : (
                        <CheckCircle size={24} weight="fill" />
                      )}
                    </div>
                    <div className="balance-amount">
                      {formatCurrency(fees.summary.balance)}
                    </div>
                    <div className="balance-details">
                      <div className="detail-row">
                        <span>Total Due:</span>
                        <span>{formatCurrency(fees.summary.total_due)}</span>
                      </div>
                      <div className="detail-row">
                        <span>Total Paid:</span>
                        <span className="text-success">{formatCurrency(fees.summary.total_paid)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Fee Items */}
                <div className="fee-items">
                  <h3>Fee Details</h3>
                  {fees.fees.length === 0 ? (
                    <div className="empty-state">No fees assigned</div>
                  ) : (
                    <div className="fee-items-list">
                      {fees.fees.map(fee => (
                        <div key={fee.id} className={`fee-item ${fee.status}`}>
                          <div className="fee-item-header">
                            <span className={`fee-type-badge ${fee.fee_type}`}>
                              {fee.fee_type}
                            </span>
                            <span className={`fee-status ${fee.status}`}>
                              {fee.status}
                            </span>
                          </div>
                          {fee.description && (
                            <p className="fee-description">{fee.description}</p>
                          )}
                          <div className="fee-item-amounts">
                            <div className="amount-row">
                              <span>Amount Due:</span>
                              <span>{formatCurrency(fee.amount_due)}</span>
                            </div>
                            <div className="amount-row">
                              <span>Amount Paid:</span>
                              <span className="text-success">{formatCurrency(fee.amount_paid)}</span>
                            </div>
                            <div className="amount-row balance">
                              <span>Balance:</span>
                              <span className={fee.balance > 0 ? 'text-danger' : ''}>
                                {formatCurrency(fee.balance)}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* Attendance View */}
        {view === 'attendance' && attendance && (
          <div className="attendance-view" data-testid="attendance-view">
            {/* Academic Term Filter */}
            {academicTerms && academicTerms.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <select 
                  value={selectedAcademicTerm || ''} 
                  onChange={(e) => setSelectedAcademicTerm(e.target.value)}
                  style={{ padding: '8px', borderRadius: '4px', border: '1px solid #0d0c0c', width: '100%', maxWidth: '300px', color: '#111827', backgroundColor: '#ffffff' }}
                  data-testid="attendance-term-filter"
                >
                  <option value="" disabled={academicTerms.length > 0}>Select Academic Term</option>
                  {academicTerms.map(term => (
                    <option key={term.id} value={term.id}>
                      {term.term} - {term.academic_year}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Summary */}
            <div className="attendance-summary-card">
              <h3>Last {attendance.period_days} Days</h3>
              <div className="attendance-summary-stats">
                <div className="att-stat present">
                  <span className="att-value">{attendance.summary.present}</span>
                  <span className="att-label">Present</span>
                </div>
                <div className="att-stat absent">
                  <span className="att-value">{attendance.summary.absent}</span>
                  <span className="att-label">Absent</span>
                </div>
                <div className="att-stat late">
                  <span className="att-value">{attendance.summary.late}</span>
                  <span className="att-label">Late</span>
                </div>
                <div className="att-stat excused">
                  <span className="att-value">{attendance.summary.excused}</span>
                  <span className="att-label">Excused</span>
                </div>
                <div className="att-stat rate">
                  <span className="att-value">{attendance.summary.attendance_rate}%</span>
                  <span className="att-label">Rate</span>
                </div>
              </div>
            </div>

            {/* Attendance Records */}
            <div className="attendance-records">
              <h3>Attendance History</h3>
              {attendance.records.length === 0 ? (
                <div className="empty-state">No attendance records found</div>
              ) : (
                <div className="records-list">
                  {attendance.records.map((record, idx) => (
                    <div key={idx} className="attendance-record">
                      <span className="record-date">
                        {new Date(record.date).toLocaleDateString('en-GB', {
                          weekday: 'short',
                          day: 'numeric',
                          month: 'short'
                        })}
                      </span>
                      <span 
                        className="record-status"
                        style={{ 
                          backgroundColor: `${getStatusColor(record.status)}20`,
                          color: getStatusColor(record.status)
                        }}
                      >
                        {record.status}
                      </span>
                      {record.remarks && (
                        <span className="record-remarks">{record.remarks}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Assignments View */}
        {view === 'assignments' && (
          <div className="assignments-view" data-testid="assignments-view">
            <StudentAssignmentsSection 
              academicTerms={academicTerms} 
              selectedAcademicTerm={selectedAcademicTerm}
              selectedAssignmentId={selectedAssignmentId}
            />
          </div>
        )}

        {/* Extra Classes View */}
        {view === 'extra-classes' && (
          <div className="extra-classes-view" data-testid="student-extra-classes-view">
            <StudentExtraClassesSection />
          </div>
        )}

        {/* E-Library View */}
        {view === 'library' && (
          <div className="library-view" data-testid="library-view">
            <StudentELibrarySection />
          </div>
        )}

        {/* Course Modules View */}
        {view === 'modules' && (
          <div className="modules-view" data-testid="modules-view">
            <StudentModulesSection onGoToAssignments={(assignmentId) => {
              setSelectedAssignmentId(assignmentId);
              setView('assignments');
            }} />
          </div>
        )}

        {/* Hostel View */}
        {view === 'hostel' && (
          <div className="hostel-view" data-testid="hostel-view">
            <HostelSection data={hostelData} />
          </div>
        )}

        {/* Transport View */}
        {view === 'transport' && (
          <div className="transport-view" data-testid="transport-view">
            <TransportSection data={transportData} />
          </div>
        )}

        {/* Report Card Preview Modal */}
        <ReportCardPreviewModal
          isOpen={showPreviewModal}
          onClose={() => setShowPreviewModal(false)}
          html={previewHTML}
          studentName={profile?.first_name || 'Student'}
          academicTerm={selectedAcademicTerm}
          studentId={profile?.id}
          onDownloadComplete={() => toast.success('Report card downloaded successfully')}
        />
      </div>
    </Layout>
  );
}

export default StudentPortalPage;
